import i18nFactory from '@yandex-int/i18n';
import * as React from 'react';

import { GridCampaignCustomPeriodBudgetCell } from '../../components/GridCampaignCustomPeriodBudgetCell/GridCampaignCustomPeriodBudgetCell';
import { GridCampaignNameCell } from '../../components/GridCampaignNameCell/GridCampaignNameCell';
import { GridCampaignRestSumCell } from '../../components/GridCampaignRestSumCell/GridCampaignRestSumCell';
import { GridCampignStrategyCell } from '../../components/GridCampaignStrategyCell/GridCampignStrategyCell';
import { GridCampaignTypeCell } from '../../components/GridCampaignTypeCell/GridCampaignTypeCell';
import { GridCampaignWeekBudgetCell } from '../../components/GridCampaignWeekBudgetCell/GridCampaignWeekBudgetCell';
import { TextCell } from '../../components/TextCell/TextCell';
import { CampaignBrandLiftCellContainer } from '../../containers/CampaignBrandLiftCellContainer/CampaignBrandLiftCellContainer';
import { EditableCampaignDateBudgetCellContainer } from '../../containers/EditableCampaignDateBudgetCellContainer/EditableCampaignDateBudgetCellContainer';
import { EditableCampaignStatusCellContainer } from '../../containers/EditableCampaignStatusCellContainer/EditableCampaignStatusCellContainer';
import { GridActionsMenuContainer } from '../../containers/GridActionsMenuContainer/GridActionsMenuContainer';
import { isCampaignEditable } from '../../mappers/campaign';
import { mapStringToCurrencySymbol } from '../../mappers/common/common';
import { getCampaignStatusTitleByType } from '../../service/CampaignHelpers/CampaignHelpers';
import {
    addSettingsToDecl,
    arrangeInOrder
} from '../../service/DeclarationHelper';
import { isStatusTextVisible } from '../../service/GridHelpers/GridHelpers';
import { StrategyHelpers } from '../../service/StrategyHelpers/StrategyHelpers';
import {
    CustomPeriodBudgetValue,
    DayBudgetValue,
    WalletDayBudgetValue,
    WeekBudgetValue
} from '../../typings/budget';
import {
    campaigns,
    ColumnsSettings,
    ColumnsSettingsViewDict
} from '../../typings/common';
import { ConstData } from '../../typings/constants';
import { DocCenterColumnTooltip } from '../../typings/doc-center';
import { Campaign, CampaignType } from '../../typings/domain';
import {
    CurrencyCode,
    GdCampaignAction,
    GdCampaignBudgetPeriod,
    GdCampaignPrimaryStatus,
    GdWalletAction
} from '../../typings/graphql-grid';
import {
    GridCampaignItemActions,
    GridCellProps,
    GridColumnDecl,
    GridColumnsOptions,
    GridScreenTotalKey,
    OptionalGridColumnDecl,
    SortDirection
} from '../../typings/grid';
import { getDefaultGoalsViewInfo, getGoalsColumnsDecl } from '../goals/goals';
import {
    getDynamicStatsColumnsFields,
    getStaticStatsColumns
} from '../stats/stats';

import * as keyset from './campaign.i18n';

const i18n = i18nFactory(keyset);

export enum GridCampaignColumnKey {
    type = 'type',
    settings = 'settings',
    name = 'name',
    state = 'state',
    strategy = 'strategy',
    platform = 'platform',
    brandlift = 'brandlift',
    budgetLimit = 'budgetLimit',
    sum = 'sum',
    id = 'id'
}

function getFixedCampaignColumns(
    clientLogin?: string
): Array<
    OptionalGridColumnDecl<
        Campaign,
        GridCampaignItemActions,
        GridColumnsOptions
    >
> {
    return [
        {
            title: '',
            key: GridCampaignColumnKey.settings,
            cellClass: 'settings-cell',
            mandatory: true,
            isSortable: false,
            fixed: true,
            minWidth: 35,
            maxWidth: 35,
            CellComponent: React.memo(
                ({
                    rowIndex,
                    actions
                }: GridCellProps<
                    Campaign,
                    GridCampaignItemActions
                >): JSX.Element | null => {
                    if (!clientLogin) {
                        return null;
                    }

                    return (
                        <GridActionsMenuContainer
                            index={rowIndex}
                            actions={actions}
                        />
                    );
                }
            ),
            isAlignedToLeft: true
        },
        {
            title: i18n('type'),
            key: GridCampaignColumnKey.type,
            cellClass: 'campaign-type-cell',
            defaultSortDirection: SortDirection.asc,
            fixed: true,
            minWidth: 35,
            maxWidth: 65,
            align: 'right',
            tooltipKey: DocCenterColumnTooltip.campaignType,
            CellComponent: React.memo(
                ({
                    rowData
                }: GridCellProps<
                    Campaign,
                    GridCampaignItemActions
                >): JSX.Element => <GridCampaignTypeCell type={rowData.type} />
            )
        },
        {
            title: i18n('name'),
            key: GridCampaignColumnKey.name,
            cellClass: 'campaign-name-cell',
            defaultSortDirection: SortDirection.asc,
            fixed: true,
            mandatory: true,
            minWidth: 150,
            maxWidth: 600,
            totalKey: GridScreenTotalKey.name,
            isAlignedToLeft: true,
            CellComponent: React.memo(
                ({
                    rowData,
                    actions
                }: GridCellProps<Campaign, GridCampaignItemActions>) => {
                    const { id, name, campaignUrl, recommendations } = rowData;

                    let recommendationsParams;

                    if (recommendations.data) {
                        recommendationsParams = {
                            items: recommendations,
                            onOpenRecommendationsPopup:
                                actions.onOpenRecommendationsPopup,
                            onSubmitRecommendation:
                                actions.onSubmitRecommendation,
                            onShowRecommendation: actions.onShowRecommendation
                        };
                    }

                    return (
                        <GridCampaignNameCell
                            key={id}
                            name={name}
                            id={id}
                            campaignUrl={campaignUrl}
                            recommendations={recommendationsParams}
                        />
                    );
                }
            )
        }
    ];
}

function getStaticCampaignColumns(
    walletActions: Array<GdWalletAction>,
    clientCurrency: CurrencyCode,
    isCompactViewEnabled: boolean,
    columnsSettings: ColumnsSettings,
    constants?: ConstData,
    clientWalletBudget?: WalletDayBudgetValue,
    isBidCorrectionSearchEnabled?: boolean
): Array<
    OptionalGridColumnDecl<
        Campaign,
        GridCampaignItemActions,
        GridColumnsOptions
    >
> {
    const currencySymbol = mapStringToCurrencySymbol(clientCurrency);

    return [
        {
            title: i18n('status'),
            key: GridCampaignColumnKey.state,
            isSortable: false,
            isAlignedToLeft: true,
            minWidth: 35,
            maxWidth: 300,
            CellComponent: React.memo(
                ({
                    rowData: {
                        type,
                        statusInfo,
                        id,
                        chiefLogin,
                        agencyId,
                        access: { actions: campaignEnabledActions },
                        budget,
                        startDate,
                        endDate
                    }
                }: GridCellProps<
                    Campaign,
                    GridCampaignItemActions
                >): JSX.Element | null => {
                    if (!constants) {
                        return null;
                    }

                    const columnViewInfo =
                        columnsSettings.columnViewInfo[
                            GridCampaignColumnKey.state
                        ];

                    // TODO: DIRECT-102216 (Временное решение, убрать, когда на бэке разберутся со статусами (ждут перехода на новую схему получения трат из БК))
                    if (
                        (type === CampaignType.INTERNAL_FREE ||
                            type === CampaignType.INTERNAL_DISTRIB) &&
                        statusInfo.type === GdCampaignPrimaryStatus.NO_MONEY
                    ) {
                        statusInfo.type = GdCampaignPrimaryStatus.ACTIVE;
                        statusInfo.hintType = undefined;
                    }

                    return (
                        <EditableCampaignStatusCellContainer
                            name={getCampaignStatusTitleByType(statusInfo.type)}
                            statusInfo={statusInfo}
                            key={id}
                            campaignId={id}
                            agencyId={agencyId}
                            canPay={campaignEnabledActions.includes(
                                GdCampaignAction.PAY
                            )}
                            columnName={'status'}
                            login={chiefLogin}
                            currency={clientCurrency}
                            dayBudgetValue={budget.value as DayBudgetValue}
                            walletBudget={clientWalletBudget}
                            dailyBudgetLimit={
                                constants.currencyConstants[clientCurrency]
                            }
                            walletActions={walletActions}
                            isTextVisible={isStatusTextVisible(
                                isCompactViewEnabled,
                                columnViewInfo
                            )}
                            startDate={startDate}
                            endDate={endDate}
                        />
                    );
                }
            )
        },
        {
            title: i18n('strategy'),
            key: GridCampaignColumnKey.strategy,
            isSortable: false,
            minWidth: 220,
            maxWidth: 400,
            CellComponent: React.memo(
                ({
                    rowData: { strategy }
                }: GridCellProps<
                    Campaign,
                    GridCampaignItemActions
                >): JSX.Element => {
                    return (
                        <GridCampignStrategyCell
                            strategy={strategy}
                            isBidCorrectionSearchEnabled={
                                isBidCorrectionSearchEnabled
                            }
                        />
                    );
                }
            )
        },
        {
            title: i18n('platform'),
            key: GridCampaignColumnKey.platform,
            isSortable: false,
            minWidth: 108,
            maxWidth: 150,
            isAvailable: (options: GridColumnsOptions): boolean => {
                return !options.hasOnlyCpmCampaigns;
            },
            CellComponent: React.memo(
                ({
                    rowData: { strategy }
                }: GridCellProps<
                    Campaign,
                    GridCampaignItemActions
                >): JSX.Element => {
                    return (
                        <TextCell capitalize={true}>
                            {StrategyHelpers.getPlatformName(strategy.platform)}
                        </TextCell>
                    );
                }
            )
        },
        {
            title: i18n('brandlift'),
            key: GridCampaignColumnKey.brandlift,
            isSortable: false,
            minWidth: 230,
            maxWidth: 230,
            isAvailable: (options: GridColumnsOptions): boolean => {
                return (
                    options.hasOnlyCpmCampaigns && options.isBrandLiftAllowed
                );
            },
            CellComponent: React.memo(
                ({
                    rowData: {
                        brandSurveyStatus,
                        brandSurveyId,
                        statusInfo: { type: status }
                    }
                }: GridCellProps<
                    Campaign,
                    GridCampaignItemActions
                >): JSX.Element | null => {
                    if (brandSurveyId && brandSurveyStatus) {
                        return (
                            <CampaignBrandLiftCellContainer
                                brandSurveyId={brandSurveyId}
                                brandSurveyStatus={brandSurveyStatus}
                                status={status}
                            />
                        );
                    }

                    return null;
                }
            )
        },
        {
            title: i18n('budget-limit-with-currency', {
                currency: currencySymbol
            }),
            titleSettings: i18n('budgetLimit'),
            key: GridCampaignColumnKey.budgetLimit,
            cellClass: 'campaign-budget-cell',
            isSortable: false,
            isAlignedToLeft: true,
            minWidth: 80,
            maxWidth: 200,
            CellComponent: React.memo(
                ({
                    rowData: campaign
                }: GridCellProps<
                    Campaign,
                    GridCampaignItemActions
                >): JSX.Element | null => {
                    const { id, budget, agencyId, chiefLogin } = campaign;
                    const campaignIsEditable = isCampaignEditable(campaign);

                    if (!constants) {
                        return null;
                    }

                    switch (budget.type) {
                        case GdCampaignBudgetPeriod.WEEK:
                            return (
                                <GridCampaignWeekBudgetCell
                                    value={budget.value as WeekBudgetValue}
                                    isCompactViewEnabled={isCompactViewEnabled}
                                />
                            );

                        case GdCampaignBudgetPeriod.DAY:
                            return (
                                <EditableCampaignDateBudgetCellContainer
                                    value={budget.value as DayBudgetValue}
                                    walletBudget={clientWalletBudget}
                                    editable={campaignIsEditable}
                                    currency={clientCurrency}
                                    key={id}
                                    agencyId={agencyId}
                                    columnName={'dayBudget'}
                                    login={chiefLogin}
                                    campaignId={id}
                                    dailyBudgetLimit={
                                        constants.currencyConstants[
                                            clientCurrency
                                        ]
                                    }
                                />
                            );
                        case GdCampaignBudgetPeriod.CUSTOM:
                            return (
                                <GridCampaignCustomPeriodBudgetCell
                                    value={
                                        budget.value as CustomPeriodBudgetValue
                                    }
                                    isCompactViewEnabled={isCompactViewEnabled}
                                />
                            );
                        case null:
                            return <TextCell>–</TextCell>;
                        default:
                            throw Error(
                                `Неподдерживаемый тип бюджета кампании ${
                                    budget.type
                                }`
                            );
                    }
                }
            )
        },
        {
            title: i18n('sum-with-currency', {
                currency: currencySymbol
            }),
            titleSettings: i18n('sum'),
            key: GridCampaignColumnKey.sum,
            isSortable: true,
            align: 'right',
            minWidth: 130,
            maxWidth: 200,
            isAvailable: (opt): boolean => !opt.isWalletEnabled,
            CellComponent: React.memo(
                ({
                    rowData: {
                        restSum,
                        needPayment,
                        moneyBlocked,
                        id,
                        access: { actions: campaignEnabledActions },
                        chiefLogin
                    }
                }: GridCellProps<
                    Campaign,
                    GridCampaignItemActions
                >): JSX.Element => {
                    return (
                        <GridCampaignRestSumCell
                            value={restSum}
                            needPayment={needPayment}
                            moneyBlocked={moneyBlocked}
                            canPay={campaignEnabledActions.includes(
                                GdCampaignAction.PAY
                            )}
                            campaignId={id}
                            chiefLogin={chiefLogin}
                        />
                    );
                }
            )
        },
        {
            title: i18n('id'),
            titleSettings: i18n('id'),
            key: GridCampaignColumnKey.id,
            isSortable: true,
            minWidth: 80,
            maxWidth: 200,
            align: 'center',
            CellComponent: React.memo(
                ({ rowData: { id } }): JSX.Element => {
                    return <div>{id}</div>;
                }
            )
        }
    ];
}

export const campaignsColumnOrder = [
    'settings',
    'type',
    'name',
    'state',
    'strategy',
    'platform',
    'brandlift',
    'budgetLimit',
    'sum',
    'GOALS',
    'STAT_COST',
    'STAT_GOALS',
    'STAT_REVENUE',
    'STAT_PROFITABILITY',
    'STAT_AVG_GOAL_COST',
    'STAT_AVG_CLICK_COST',
    'STAT_SHOWS',
    'STAT_CPM_PRICE',
    'STAT_CLICKS',
    'STAT_CTR',
    'STAT_CONVERSION_RATE',
    'STAT_BOUNCE_RATE',
    'id'
];

export function getDefaultDynamicCampaignColumnsFields(): ColumnsSettings {
    return {
        columnOrder: campaignsColumnOrder,
        columnViewInfo: {
            settings: {
                width: 35,
                widthCompact: 35,
                hidden: false
            },
            type: {
                width: 35,
                widthCompact: 35,
                hidden: false
            },
            name: {
                width: 250,
                widthCompact: 250,
                hidden: false
            },
            state: {
                width: 200,
                widthCompact: 200,
                hidden: false
            },
            strategy: {
                width: 240,
                widthCompact: 240,
                hidden: false
            },
            platform: {
                width: 108,
                widthCompact: 108,
                hidden: false
            },
            brandlift: {
                width: 230,
                widthCompact: 230,
                hidden: false
            },
            budgetLimit: {
                width: 150,
                widthCompact: 150,
                hidden: false
            },
            sum: {
                width: 130,
                widthCompact: 130,
                hidden: false
            },
            id: {
                width: 100,
                widthCompact: 100,
                hidden: true
            },
            ...getDefaultGoalsViewInfo(),
            ...getDynamicStatsColumnsFields()
        }
    };
}

export function getCampaignColumns(
    options: GridColumnsOptions,
    columnsSettings: ColumnsSettingsViewDict,
    walletActions: Array<GdWalletAction>,
    currency: CurrencyCode,
    isCompactViewEnabled: boolean,
    isGoalsStatEnabled: boolean,
    hasSelectedGoals: boolean,
    constants?: ConstData,
    clientLogin?: string,
    clientWalletBudget?: WalletDayBudgetValue,
    hasSelectedGoalsAtColumnsSettingsEditor?: boolean,
    isColumnsSettingsEditorLocation?: boolean,
    isBidCorrectionSearchEnabled?: boolean
): Array<GridColumnDecl<Campaign>> {
    const fixedColumns = getFixedCampaignColumns(clientLogin).map(column => {
        return addSettingsToDecl(column, columnsSettings, campaigns);
    });

    const result = getStaticCampaignColumns(
        walletActions,
        currency,
        isCompactViewEnabled,
        columnsSettings[campaigns],
        constants,
        clientWalletBudget,
        isBidCorrectionSearchEnabled
    )
        .concat(
            getGoalsColumnsDecl<Campaign>(
                hasSelectedGoalsAtColumnsSettingsEditor,
                isColumnsSettingsEditorLocation
            )
        )
        .concat(
            getStaticStatsColumns<Campaign>(
                {
                    getCurrency: (item: Campaign): CurrencyCode => item.currency
                },
                currency,
                isCompactViewEnabled,
                hasSelectedGoals,
                isGoalsStatEnabled,
                hasSelectedGoalsAtColumnsSettingsEditor,
                isColumnsSettingsEditorLocation
            )
        )
        .filter(column => {
            return column.isAvailable ? column.isAvailable(options) : true;
        })
        .map(column => {
            return addSettingsToDecl(column, columnsSettings, campaigns);
        });

    return fixedColumns.concat(
        arrangeInOrder(result, columnsSettings[campaigns].columnOrder, true)
    );
}
